import { useEffect, useRef, useState } from 'react';
import ConnectGoogleDriveButton from '../AppDrawer/Google/Google';
import GDrive from '../../assets/images/gDrive.svg';
import { IoIosArrowDown } from 'react-icons/io';

import {
  useLazyGetJobsByCompanyQuery,
  useUploadResumesMutation,
  useLazyGetGoogleDriveConnectionStatusQuery,
  useLazyGetResumesByCompanyIdQuery,
} from '../../features/apis/intergration';
import ReactToastify from '../../customHook/useReactToastify';
import useCurrentUser from '../../customHook/useCurrentUser';

const UploadIcon = () => (
  <svg
    width="48"
    height="48"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#BAA2FF"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="16 16 12 12 8 16" />
    <line x1="12" y1="12" x2="12" y2="21" />
    <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
  </svg>
);

const CircularProgress = ({ value }: { value: number }) => {
  const r = 18;
  const circ = 2 * Math.PI * r;

  return (
    <div className="relative w-12 h-12 flex items-center justify-center">
      <svg className="absolute w-12 h-12 -rotate-90">
        <circle
          cx="24"
          cy="24"
          r={r}
          fill="none"
          stroke="#e5e7eb"
          strokeWidth="3"
        />
        <circle
          cx="24"
          cy="24"
          r={r}
          fill="none"
          stroke="#E3D9FF"
          strokeWidth="3"
          strokeDasharray={circ}
          strokeDashoffset={circ - (value / 100) * circ}
          strokeLinecap="round"
        />
      </svg>
      <span className="text-[9px] font-bold text-gray-500 z-10">{value}%</span>
    </div>
  );
};

export function DataSourceTab() {
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [selectedJobId, setSelectedJobId] = useState<string>('');
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [jobDropdownOpen, setJobDropdownOpen] = useState(false);
  const [getJobsByCompany, { data: jobs }] = useLazyGetJobsByCompanyQuery();
  const [getResumesByCompanyId] = useLazyGetResumesByCompanyIdQuery();
  const [uploadResumes, { isLoading }] = useUploadResumesMutation();
  const [getGoogleDriveConnectionStatus, { data: googleDriveStatus }] =
    useLazyGetGoogleDriveConnectionStatusQuery();
  const user = useCurrentUser();

  useEffect(() => {
    if (user?.companyId) {
      getJobsByCompany(user.companyId);
      getGoogleDriveConnectionStatus(user.companyId);
    }
  }, [getGoogleDriveConnectionStatus, getJobsByCompany, user?.companyId]);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const MAX_FILES = 10;
  const MAX_SIZE = 10 * 1024 * 1024;

  const sources =
    googleDriveStatus?.googleSyncHistory?.map((item: any, index: number) => ({
      id: item._id,
      name: `Google Drive Sync ${index + 1}`,
      dates: new Date(item.syncedAt).toLocaleString(),
      progress: item.completion,
      notes: item.notes,
    })) || [];

  const handleUploadClick = () => fileInputRef.current?.click();

  const handleFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const files = Array.from(e.target.files);
    if (files.length > MAX_FILES) {
      ReactToastify({
        success: false,
        message: 'You can upload a maximum of 10 resumes.',
      });
      return;
    }

    const validFiles = files.filter((file) => {
      const isPDF = file.type === 'application/pdf';
      const validSize = file.size <= MAX_SIZE;
      if (!isPDF) {
        ReactToastify({
          success: false,
          message: `${file.name} is not a PDF file.`,
        });
        return false;
      }
      if (!validSize) {
        ReactToastify({
          success: false,
          message: `${file.name} is too large.`,
        });
        return false;
      }
      return true;
    });

    setUploadedFiles(validFiles);
  };

  const handleRoleSelect = (job: any) => {
    setSelectedRole(job.jobTitle);
    setSelectedJobId(job._id);
    setJobDropdownOpen(false);
  };

  const handleSubmit = async () => {
    if (!selectedJobId)
      return ReactToastify({
        success: false,
        message: 'Please select a job role',
      });
    if (!uploadedFiles.length)
      return ReactToastify({
        success: false,
        message: 'Please upload at least one resume',
      });

    const formData = new FormData();
    uploadedFiles.forEach((file) => formData.append('resumes', file));

    try {
      await uploadResumes({ jobId: selectedJobId, data: formData }).unwrap();
      ReactToastify({
        success: true,
        message: 'Resumes uploaded successfully',
      });
      setUploadedFiles([]);
      setSelectedRole('');
      setSelectedJobId('');
      getResumesByCompanyId(user?.companyId).unwrap();
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (error) {
      console.error(error);
      ReactToastify({ success: false, message: 'Upload failed' });
    }
  };

  return (
    <div className="flex flex-col lg:flex-row gap-3 sm:gap-4 md:gap-6 w-full mt-4 md:mt-6">
      {/* LEFT */}
      <div className="w-full lg:w-1/3 flex-shrink-0 flex flex-col gap-3 sm:gap-4">
        <h2 className="text-sm xl:text-[15px] font-semibold text-gray-900">
          Upload Resume(s) or Connect Drive
        </h2>

        <div className="bg-white  border-[#D4D4D4] border-[0.5px] rounded-xl p-4 xl:p-6 flex flex-col gap-3 sm:gap-4">
          <div className="relative">
            <div
              className=" border-[#D4D4D4] border-[0.5px] rounded-lg px-3 py-2 flex justify-between items-center cursor-pointer"
              onClick={() => setJobDropdownOpen(!jobDropdownOpen)}
            >
              <span className="text-[14px] text-gray-600">
                {selectedRole || 'Select Job Role'}
              </span>
              <IoIosArrowDown size={20} className="text-gray-600" />
            </div>

            {jobDropdownOpen && (
              <div className="absolute z-10 w-full bg-white  border-[#D4D4D4] border-[0.5px] rounded-md mt-1 shadow-md min-h-14 px-4 sm:px-6 py-3 sm:py-4">
                {jobs && jobs.length > 0 ? (
                  jobs.map((job) => (
                    <div
                      key={job.id}
                      className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                      onClick={() => handleRoleSelect(job)}
                    >
                      {job.jobTitle}
                    </div>
                  ))
                ) : (
                  <div className="text-gray-400 text-sm text-center py-3 h-28">
                    No jobs added yet
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="border-[0.5px] border-dashed border-[#D4D4D4] rounded-xl px-2 lg:px-5 py-6 sm:py-8 flex flex-col items-center gap-3 bg-[#F8F8F8] cursor-pointer">
            <div
              className="w-20 h-20 rounded-full bg-[#E3D9FF] flex items-center justify-center"
              onClick={handleUploadClick}
            >
              <UploadIcon />
            </div>

            <div className="text-center" onClick={handleUploadClick}>
              <p className="font-bold text-[15px] text-gray-900 mb-1">
                Upload Resume(s)
              </p>
              <p className="text-[12px] text-gray-400">
                Upload up to 10 PDF resumes
                <br />
                Max size: 10MB each
              </p>
            </div>

            {uploadedFiles.length > 0 && (
              <ul className="text-[12px] text-gray-600 mt-2 max-h-24 overflow-y-auto  border-[#D4D4D4] border-[0.5px] rounded-md p-2 w-full break-words">
                {uploadedFiles.map((file, i) => (
                  <li key={i} className="truncate w-full" title={file.name}>
                    {file.name}
                  </li>
                ))}
              </ul>
            )}

            {/* Button with tooltip */}
            <div className="relative group w-full sm:w-2/3 xl:w-1/2">
              <button
                disabled={
                  !selectedRole || uploadedFiles.length === 0 || isLoading
                }
                className={`w-full text-white font-semibold text-[14px] py-2 rounded-lg flex items-center justify-center gap-2
                  ${
                    selectedRole && uploadedFiles.length && !isLoading
                      ? 'bg-[linear-gradient(90deg,#B79DFF,#3800D7)]'
                      : 'bg-[#3800D7]/20 cursor-not-allowed'
                  }`}
                onClick={handleSubmit}
              >
                {isLoading ? (
                  <>
                    <span className="animate-spin w-4 h-4 border-[#D4D4D4] border-[0.5px] border-t-transparent rounded-full" />
                    Uploading...
                  </>
                ) : (
                  'Submit CV'
                )}
              </button>

              {!selectedRole && (
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:flex items-center whitespace-nowrap bg-gray-800 text-white text-[11px] px-2.5 py-1.5 rounded-lg shadow-md pointer-events-none">
                  Please select a job role
                  <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-800" />
                </div>
              )}
            </div>

            <input
              type="file"
              multiple
              accept=".pdf"
              ref={fileInputRef}
              className="hidden"
              onChange={handleFilesSelected}
            />
          </div>

          <div className="flex items-center gap-2">
            <div className="flex-1 h-px bg-[#D4D4D4]" />
            <span className="text-[13px] text-gray-400">Or</span>
            <div className="flex-1 h-px bg-[#D4D4D4]" />
          </div>

          <ConnectGoogleDriveButton />
        </div>
      </div>

      {/* RIGHT */}
      <div className="flex-1 flex flex-col gap-3 sm:gap-4">
        <div>
          <h2 className="text-[15px] font-semibold text-[#7c3aed]">
            Pipelines and Workflows
          </h2>
          <p className="text-[14px] text-gray-700 my-2">
            Recently Uploaded or Linked Sources
          </p>
        </div>

        <div className="bg-white border-[#D4D4D4] border-[0.5px] rounded-xl p-4 sm:p-5 flex flex-col flex-1">
          <div className="flex-1 overflow-y-auto space-y-3 sm:space-y-4 max-h-[400px] hide-scrollbar">
            {sources && sources.length > 0 ? (
              sources.map((src) => (
                <div
                  key={src.id}
                  className=" border-[#D4D4D4] border-[0.5px] rounded-xl p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 cursor-pointer hover:shadow-md"
                >
                  <img src={GDrive} className="w-10 h-10" />
                  <div className="flex-1">
                    <p className="font-semibold text-[14px] text-gray-900 mb-1">
                      {src.name}
                    </p>
                    <div className="flex gap-3 flex-wrap">
                      <span className="text-[11px] text-[#7c3aed]">
                        🕐 {src.dates}
                      </span>
                    </div>
                  </div>
                  <CircularProgress value={src.progress} />
                </div>
              ))
            ) : (
              <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-20 h-20 mb-4 text-[#B79DFF]"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3 7a2 2 0 012-2h5l2 3h9a2 2 0 012 2v7a2 2 0 01-2 2H5a2 2 0 01-2-2V7z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 11v6m0 0l-3-3m3 3l3-3"
                  />
                </svg>
                <p className="font-semibold text-[15px] mb-2 text-gray-800">
                  No uploads or linked sources yet
                </p>
                <p className="text-[13px] text-gray-500 text-center max-w-xs">
                  Upload resumes or connect your Google Drive to see them appear
                  here.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
